import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Calendar, ChevronLeft, ChevronRight, Users, Truck, Save, Loader2, X, UserPlus } from "lucide-react";
import { fmtRoDate } from "../../lib/dateFmt";
import { errMsg } from "../../lib/apiErrors";

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const DAY_NAMES = ["Duminică", "Luni", "Marți", "Miercuri", "Joi", "Vineri", "Sâmbătă"];
const dayLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return DAY_NAMES[d.getDay()];
};

const fmtAudit = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    const time = d.toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" });
    if (sameDay) return `astăzi ${time}`;
    return `${d.toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit" })} ${time}`;
  } catch { return ""; }
};

export default function ProjectHoursView({ canEdit }) {
  const [date, setDate] = useState(todayIso());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // item object to open in dialog

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/project-hours/by-day?date=${date}`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(errMsg(e, "Eroare la încărcare proiecte"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const totalPersons = useMemo(() => items.reduce((s, it) => s + (it.persons_count || 0), 0), [items]);
  const totalHours = useMemo(() => items.reduce((s, it) => s + (it.total_hours || 0), 0), [items]);
  const totalDriven = useMemo(() => items.reduce((s, it) => s + (it.total_hours_driven || 0), 0), [items]);

  return (
    <div data-testid="project-hours-view">
      <div className="card-flat p-3 mb-3 flex items-center gap-3 flex-wrap">
        <Calendar size={18} className="text-[var(--primary)]"/>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDate(addDays(date, -1))}
          data-testid="ph-prev-day"
        >
          <ChevronLeft size={14}/>
        </Button>
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="h-9 w-[170px]"
          data-testid="ph-date-input"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDate(addDays(date, 1))}
          data-testid="ph-next-day"
        >
          <ChevronRight size={14}/>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDate(todayIso())}
          data-testid="ph-today"
        >
          Azi
        </Button>
        <span className="text-sm font-semibold text-zinc-700 ml-2" data-testid="ph-date-label">
          {dayLabel(date)} · {fmtRoDate(date)}
        </span>
        <div className="ml-auto flex items-center gap-3 text-[11px] text-zinc-600">
          <span><strong>{items.length}</strong> proiecte/regii</span>
          <span><strong>{totalPersons}</strong> persoane</span>
          <span className="text-emerald-700"><strong>{totalHours.toFixed(1)}h</strong> lucru</span>
          <span className="text-blue-700"><strong>{totalDriven.toFixed(1)}h</strong> condus</span>
        </div>
      </div>

      {loading ? (
        <div className="card-flat p-12 text-center text-zinc-500">
          <Loader2 className="inline animate-spin mr-2" size={18}/> Se încarcă...
        </div>
      ) : items.length === 0 ? (
        <div className="card-flat p-12 text-center text-zinc-500 italic" data-testid="ph-empty">
          Nicio alocare planificată pe <strong>{fmtRoDate(date)}</strong>.
          Alocă proiecte/regii din pagina Planificator pentru a putea înregistra ore aici.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="ph-items-grid">
          {items.map((it) => {
            const totalH = it.total_hours || 0;
            const totalD = it.total_hours_driven || 0;
            const hasData = totalH > 0 || totalD > 0;
            return (
              <button
                key={it.schedule_id}
                type="button"
                onClick={() => setSelected(it)}
                className="text-left rounded-lg border-2 p-3 transition-all hover:shadow-md"
                style={{
                  borderColor: hasData ? "#10b981" : it.color,
                  background: hasData ? "#ecfdf5" : "white",
                }}
                data-testid={`ph-item-${it.ref_id}`}
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="font-display font-extrabold text-base leading-tight" style={{ color: hasData ? "#059669" : it.color }}>
                    {it.label}
                  </div>
                  {hasData && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded">
                      ✓ ore
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap text-[11px] text-zinc-600">
                  {it.kind === "regie" && <span className="font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded text-[9px]">REGIE</span>}
                  {it.is_dismantle && <span className="font-bold uppercase tracking-wider bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded text-[9px]">DEMONTAJ</span>}
                  {it.firma && <span>🏢 {it.firma}</span>}
                  {it.adresa && <span>📍 {it.adresa}</span>}
                  {it.vorarbeiter && <span>👷 {it.vorarbeiter}</span>}
                </div>
                <div className="flex items-center gap-3 mt-2 pt-2 border-t text-[11px]" style={{ borderColor: `${it.color}30` }}>
                  <span className="inline-flex items-center gap-1 font-semibold text-zinc-700">
                    <Users size={12}/> {it.persons_count} pers
                  </span>
                  <span className="inline-flex items-center gap-1 font-semibold text-emerald-700">
                    ⏱ {totalH.toFixed(1)}h
                  </span>
                  {totalD > 0 && (
                    <span className="inline-flex items-center gap-1 font-semibold text-blue-700">
                      <Truck size={12}/> {totalD.toFixed(1)}h
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <ProjectHoursDialog
          item={selected}
          date={date}
          canEdit={canEdit}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}

function ProjectHoursDialog({ item, date, canEdit, onClose, onSaved }) {
  const [rows, setRows] = useState(() => (item.persons || []).map((p) => ({
    person_id: p.id,
    name: p.name,
    is_driver: p.is_driver,
    permis_conducere: p.permis_conducere,
    is_original: true,
    hours: p.hours || "",
    hours_driven: p.hours_driven || "",
    note: p.note || "",
    logged_by_name: p.logged_by_name || null,
    logged_at: p.logged_at || null,
    action: p.action || null,
  })));
  // Persoane scoase din list (originale care nu vor mai fi listate) — la save, hours=0 → delete
  const [removedOriginalIds, setRemovedOriginalIds] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [bulkHours, setBulkHours] = useState("");
  const [allPersons, setAllPersons] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerValue, setPickerValue] = useState("");

  useEffect(() => {
    // Fetch all active persons pentru add-dropdown (o singură dată la deschidere)
    api.get("/persons")
      .then((r) => setAllPersons((r.data || []).filter((p) => p.active !== false)))
      .catch(() => setAllPersons([]));
  }, []);

  const driverIdx = rows.findIndex((r) => r.is_driver);
  const currentIds = useMemo(() => new Set(rows.map((r) => r.person_id)), [rows]);
  const availableToAdd = useMemo(() => {
    return allPersons
      .filter((p) => !currentIds.has(p.id))
      .sort((a, b) => `${a.nume || ""} ${a.prenume || ""}`.localeCompare(`${b.nume || ""} ${b.prenume || ""}`, "ro"));
  }, [allPersons, currentIds]);

  const updateRow = (idx, patch) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeRow = (idx) => {
    const r = rows[idx];
    if (!r) return;
    // Dacă era original → marcheaz-o ca „de șters la save" (hours=0 prin backend)
    if (r.is_original) {
      setRemovedOriginalIds((s) => {
        const n = new Set(s);
        n.add(r.person_id);
        return n;
      });
    }
    setRows((rs) => rs.filter((_, i) => i !== idx));
  };

  const restoreOriginal = (pid) => {
    // Re-adaugă în UI o persoană originală scoasă
    const orig = (item.persons || []).find((p) => p.id === pid);
    if (!orig) return;
    setRemovedOriginalIds((s) => {
      const n = new Set(s);
      n.delete(pid);
      return n;
    });
    setRows((rs) => [...rs, {
      person_id: orig.id,
      name: orig.name,
      is_driver: orig.is_driver,
      permis_conducere: orig.permis_conducere,
      is_original: true,
      hours: orig.hours || "",
      hours_driven: orig.hours_driven || "",
      note: orig.note || "",
    }]);
  };

  const addPersonById = (pid) => {
    if (!pid) return;
    if (currentIds.has(pid)) return;
    const p = allPersons.find((x) => x.id === pid);
    if (!p) return;
    setRows((rs) => [...rs, {
      person_id: p.id,
      name: `${p.nume || ""} ${p.prenume || ""}`.trim() || "(fără nume)",
      is_driver: false,
      permis_conducere: !!p.permis_conducere,
      is_original: false,
      hours: "",
      hours_driven: "",
      note: "",
    }]);
    // Dacă fusese marcată ca „eliminată original" → curăță
    setRemovedOriginalIds((s) => {
      if (!s.has(pid)) return s;
      const n = new Set(s);
      n.delete(pid);
      return n;
    });
    setPickerValue("");
    setPickerOpen(false);
    toast.success(`${p.nume} ${p.prenume} adăugat(ă)`);
  };

  const applyToAll = () => {
    const v = parseFloat(bulkHours);
    if (Number.isNaN(v) || v < 0) {
      toast.error("Introdu un număr valid de ore");
      return;
    }
    setRows((rs) => rs.map((r) => ({ ...r, hours: String(v) })));
    toast.success(`Aplicat ${v}h pe toți cei ${rows.length}`);
  };

  const totalHours = rows.reduce((s, r) => s + (parseFloat(r.hours) || 0), 0);
  const totalDriven = rows.reduce((s, r) => s + (parseFloat(r.hours_driven) || 0), 0);

  const submit = async () => {
    if (!canEdit) { toast.error("Fără permisiune de editare"); return; }
    setBusy(true);
    try {
      // Combinăm rows active cu cele marcate pentru ștergere (trimitem hours=0)
      const submitRows = [
        ...rows.map((r) => ({
          person_id: r.person_id,
          hours: parseFloat(r.hours) || 0,
          hours_driven: parseFloat(r.hours_driven) || 0,
          note: (r.note || "").trim() || null,
        })),
        ...Array.from(removedOriginalIds).map((pid) => ({
          person_id: pid,
          hours: 0,
          hours_driven: 0,
          note: null,
        })),
      ];
      const payload = {
        date,
        kind: item.kind,
        ref_id: item.ref_id,
        label: item.label,
        color: item.color,
        car_id: item.car_ids?.[0] || null,
        rows: submitRows,
      };
      const res = await api.put("/project-hours/by-day", payload);
      const u = res.data?.upserts || 0;
      const d = res.data?.deletes || 0;
      const msg = [];
      if (u > 0) msg.push(`${u} ${u === 1 ? "alocare" : "alocări"} salvate`);
      if (d > 0) msg.push(`${d} șterse`);
      toast.success(msg.join(", ") || "Nicio modificare");
      onSaved && onSaved();
    } catch (e) {
      toast.error(errMsg(e, "Eroare la salvare"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose && onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="ph-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ background: item.color }}/>
            {item.label}
            {item.kind === "regie" && <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">REGIE</span>}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Înregistrează orele lucrate pe {fmtRoDate(date)}. <span className="inline-block w-2 h-2 rounded-sm bg-orange-400 mx-0.5 align-middle"/> alocați din Planificator, <span className="inline-block w-2 h-2 rounded-sm bg-emerald-400 mx-0.5 align-middle"/> adăugați manual. Șoferul desemnat (🚗) primește în plus ore de condus.
          </DialogDescription>
        </DialogHeader>

        {canEdit && (
          <div className="flex items-center gap-2 p-2 bg-zinc-50 rounded mb-2 flex-wrap">
            <Label className="text-[11px] font-semibold text-zinc-700">Aplică ore identice pe toți:</Label>
            <Input
              type="number"
              step="0.25"
              min="0"
              value={bulkHours}
              onChange={(e) => setBulkHours(e.target.value)}
              className="h-7 w-20 text-sm"
              placeholder="ex: 8"
              data-testid="ph-bulk-input"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={applyToAll}
              disabled={!bulkHours || rows.length === 0}
              data-testid="ph-bulk-apply"
            >
              Aplică pe toți
            </Button>
            <span className="text-[10px] text-zinc-500 ml-2 hidden md:inline">(suprascrie valorile curente; orele de condus rămân)</span>

            <div className="ml-auto flex items-center gap-1.5">
              {!pickerOpen ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPickerOpen(true)}
                  disabled={availableToAdd.length === 0}
                  className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                  data-testid="ph-add-person-btn"
                >
                  <UserPlus size={14} className="mr-1"/> Adaugă persoană
                </Button>
              ) : (
                <div className="flex items-center gap-1.5" data-testid="ph-add-person-row">
                  <Select value={pickerValue} onValueChange={(v) => { setPickerValue(v); addPersonById(v); }}>
                    <SelectTrigger className="h-8 min-w-[220px] text-sm" data-testid="ph-add-person-select">
                      <SelectValue placeholder="Selectează persoană..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableToAdd.length === 0 ? (
                        <div className="px-2 py-1 text-xs text-zinc-400 italic">Toți sunt deja în listă</div>
                      ) : (
                        availableToAdd.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.nume} {p.prenume}
                            {p.permis_conducere && <span className="text-blue-600 ml-1">🚗</span>}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    onClick={() => { setPickerOpen(false); setPickerValue(""); }}
                    className="text-zinc-400 hover:text-zinc-700"
                    title="Închide"
                    data-testid="ph-add-person-close"
                  >
                    <X size={14}/>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {rows.length === 0 ? (
          <div className="py-8 text-center text-zinc-500 italic" data-testid="ph-rows-empty">
            Lista e goală.
            {canEdit && availableToAdd.length > 0 && (
              <span> Apasă <strong>&bdquo;Adaugă persoană&rdquo;</strong> pentru a începe.</span>
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto pr-1 space-y-1.5" data-testid="ph-rows-list">
            <div className="grid grid-cols-[1fr_90px_110px_1fr_32px] gap-2 text-[10px] uppercase tracking-wider font-bold text-zinc-500 px-2">
              <div>Persoană</div>
              <div className="text-right">Ore lucru</div>
              <div className="text-right">Ore condus</div>
              <div>Notă</div>
              <div></div>
            </div>
            {rows.map((r, i) => {
              const isOrig = r.is_original;
              const rowBg = r.is_driver
                ? "bg-blue-50 border-blue-300"
                : isOrig
                  ? "border-orange-300 bg-orange-50/40"
                  : "border-emerald-300 bg-emerald-50/40";
              return (
                <div
                  key={r.person_id}
                  className={`grid grid-cols-[1fr_90px_110px_1fr_32px] gap-2 items-start px-2 py-1.5 rounded border-2 ${rowBg}`}
                  data-testid={`ph-row-${r.person_id}`}
                >
                  <div className="text-sm font-medium truncate flex flex-col gap-0.5 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                    {r.is_driver && <span className="text-blue-600 text-[12px]" title="Șofer desemnat din Planificator">🚗</span>}
                    {!r.is_driver && r.permis_conducere && <span className="text-zinc-400 text-[11px]" title="Are permis de conducere">🚗</span>}
                    <span className="truncate">{r.name}</span>
                    {r.is_driver && <span className="text-[9px] font-bold uppercase tracking-wider bg-blue-200 text-blue-900 px-1 py-0.5 rounded">ȘOFER</span>}
                    {!r.is_driver && isOrig && (
                      <span className="text-[9px] font-bold uppercase tracking-wider bg-orange-200 text-orange-900 px-1 py-0.5 rounded" data-testid={`ph-badge-original-${r.person_id}`}>
                        📌 ALOCAT
                      </span>
                    )}
                    {!isOrig && (
                      <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-200 text-emerald-900 px-1 py-0.5 rounded" data-testid={`ph-badge-new-${r.person_id}`}>
                        ➕ NOU
                      </span>
                    )}
                    </div>
                    {r.logged_by_name && (
                      <div className="text-[10px] text-zinc-500 flex items-center gap-1 truncate" data-testid={`ph-audit-${r.person_id}`} title={`${r.action === "update" ? "Modificat" : "Înregistrat"} de ${r.logged_by_name} la ${fmtAudit(r.logged_at)}`}>
                        <span className="text-[9px]">{r.action === "update" ? "✏️" : "⏰"}</span>
                        <span className="truncate"><b className="text-zinc-700">{r.logged_by_name}</b> · {fmtAudit(r.logged_at)}</span>
                      </div>
                    )}
                    {!r.logged_by_name && (parseFloat(r.hours) > 0 || parseFloat(r.hours_driven) > 0) && (
                      <div className="text-[10px] text-zinc-400 italic" data-testid={`ph-audit-legacy-${r.person_id}`}>
                        (înregistrare mai veche, fără audit)
                      </div>
                    )}
                  </div>
                  <Input
                    type="number"
                    step="0.25"
                    min="0"
                    value={r.hours}
                    onChange={(e) => updateRow(i, { hours: e.target.value })}
                    className="h-8 text-sm text-right"
                    placeholder="0"
                    disabled={!canEdit}
                    data-testid={`ph-hours-${r.person_id}`}
                  />
                  <Input
                    type="number"
                    step="0.25"
                    min="0"
                    value={r.hours_driven}
                    onChange={(e) => updateRow(i, { hours_driven: e.target.value })}
                    className={`h-8 text-sm text-right ${r.is_driver ? "border-blue-400 bg-blue-50/50" : ""}`}
                    placeholder={r.is_driver ? "ex: 1" : "0"}
                    disabled={!canEdit}
                    title={r.is_driver ? "Ore de condus (magazie → șantier și retur) — se plătesc în plus" : "Ore extra dacă persoana a condus"}
                    data-testid={`ph-driven-${r.person_id}`}
                  />
                  <Input
                    value={r.note || ""}
                    onChange={(e) => updateRow(i, { note: e.target.value })}
                    className="h-8 text-xs"
                    placeholder="Notă (opțional)"
                    disabled={!canEdit}
                    data-testid={`ph-note-${r.person_id}`}
                  />
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      className="text-zinc-400 hover:text-red-600 transition-colors w-8 h-8 grid place-items-center rounded hover:bg-red-50"
                      title={isOrig ? "Scoate (n-a venit la lucru) — la save se șterg orele acestei persoane pe acest proiect" : "Scoate (anulează adăugarea)"}
                      data-testid={`ph-remove-${r.person_id}`}
                    >
                      <X size={14}/>
                    </button>
                  )}
                </div>
              );
            })}

            {/* Lista persoanelor originale scoase — restore option */}
            {removedOriginalIds.size > 0 && canEdit && (
              <div className="mt-2 p-2 rounded border border-dashed border-orange-300 bg-orange-50/30 text-[11px]" data-testid="ph-removed-block">
                <div className="font-semibold text-orange-800 mb-1.5">
                  Scoși ({removedOriginalIds.size}) — vor avea orele șterse la salvare. Click pe nume să-i re-adaugi:
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {Array.from(removedOriginalIds).map((pid) => {
                    const p = (item.persons || []).find((x) => x.id === pid);
                    if (!p) return null;
                    return (
                      <button
                        key={pid}
                        type="button"
                        onClick={() => restoreOriginal(pid)}
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-white border border-orange-300 text-orange-800 hover:bg-orange-100 transition-colors"
                        data-testid={`ph-restore-${pid}`}
                      >
                        ↩ {p.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-2 pt-2 border-t flex items-center justify-between text-sm">
          <div className="flex gap-4">
            <span className="font-semibold text-emerald-700">
              Total lucru: <span className="tabular-nums">{totalHours.toFixed(2)}h</span>
            </span>
            {totalDriven > 0 && (
              <span className="font-semibold text-blue-700">
                Total condus: <span className="tabular-nums">{totalDriven.toFixed(2)}h</span>
              </span>
            )}
            {rows.filter((r) => !r.is_original).length > 0 && (
              <span className="font-semibold text-emerald-700">
                ➕ {rows.filter((r) => !r.is_original).length} adăugați
              </span>
            )}
          </div>
          {driverIdx === -1 && rows.some((r) => r.permis_conducere) && (
            <span className="text-[11px] text-amber-700">⚠ Niciun șofer desemnat în Planificator — alege manual</span>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 pt-2 border-t">
          <Button variant="outline" onClick={onClose} disabled={busy}>Anulează</Button>
          {canEdit && (
            <Button
              onClick={submit}
              disabled={busy || (rows.length === 0 && removedOriginalIds.size === 0)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="ph-save"
            >
              {busy ? <><Loader2 className="animate-spin mr-1" size={14}/>Se salvează...</> : <><Save size={14} className="mr-1"/>Salvează</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
